import { ApButtonDanger, ApButtonMain } from '@alixpartners/ui-components';
import * as React from 'react';
import {
  IndustryFormActionRow,
  SecondaryBlendButton,
  SectionDescription,
  SectionTitle,
} from './Benchmarks.styles';
import { IndustryBlendForm } from './IndustryBlendForm';
import {
  blendAPIToLocal,
  blendLocalToAPI,
  getTotalAllocation,
  isValidLocalBlend,
} from './Benchmarks.helpers';
import {
  IProviderIndustry,
  IApIndustry,
  INDUSTRY_PROVIDERS,
  IIndustryBlend,
  IIndustryBlendLocal,
  IIndustryAllocationLocal,
} from 'utils/types';
import isEqual from 'lodash/isEqual';

const getEmptyIndustry = (
  totalAllocation: number
): IIndustryAllocationLocal => {
  return {
    apIndustryCode: '',
    gartnerIndustryCode: '',
    computerEconomicsIndustryCode: '',
    allocationPercentage: totalAllocation >= 100 ? 0 : 100 - totalAllocation,
  };
};

interface INewIndustryBlendProps {
  apIndustries: IApIndustry[];
  availableYears: number[];
  calculateBlend: (o: IIndustryBlend) => void;
  industryBlend?: IIndustryBlend;
  industryBlends: IIndustryBlend[];
  missingDataProviders: any[];
  missingProviders: any[];
  onCancel: () => void;
  onCreate: (o: IIndustryBlend) => void;
  onCreateAndApply: (o: IIndustryBlend) => void;
  providerIndustries: IProviderIndustry[];
}

export class NewIndustryBlend extends React.Component<
  INewIndustryBlendProps,
  IIndustryBlendLocal
> {
  state = {
    benchmarkBreakDown: undefined,
    id: undefined,
    industryAllocations: [getEmptyIndustry(0)],
    isActive: false,
    name: '',
    revenueUpliftPercentage: '',
    year: 0,
  };

  componentDidMount() {
    const { availableYears, industryBlend } = this.props;
    if (industryBlend) {
      this.setState(blendAPIToLocal(industryBlend));
    }
    if (availableYears) {
      this.setState({ year: Math.max.apply(null, availableYears) });
    }
  }

  componentDidUpdate(
    prevProps: INewIndustryBlendProps,
    prevState: IIndustryBlendLocal
  ) {
    const { availableYears, calculateBlend, industryBlend } = this.props;
    if (industryBlend && !isEqual(prevProps.industryBlend, industryBlend)) {
      this.setState(blendAPIToLocal(industryBlend));
    }
    if (availableYears && !prevProps.availableYears) {
      this.setState({ year: Math.max.apply(null, availableYears) });
    }
    if (isValidLocalBlend(this.state) && !isEqual(prevState, this.state)) {
      calculateBlend(blendLocalToAPI(this.state));
    }
  }

  updateIndustriesAllocation = (
    id: number,
    industry: IIndustryAllocationLocal
  ) => {
    const { industryAllocations } = this.state;
    const newIndustryAllocations = [...industryAllocations];
    newIndustryAllocations.splice(id, 1, industry);
    this.setState({ industryAllocations: newIndustryAllocations });
  };

  render() {
    const {
      apIndustries,
      availableYears,
      industryBlend,
      industryBlends,
      missingDataProviders,
      missingProviders,
      onCancel,
      onCreate,
      onCreateAndApply,
      providerIndustries,
    } = this.props;
    const {
      industryAllocations,
      revenueUpliftPercentage,
      year,
      name,
    } = this.state;
    const totalAllocation = getTotalAllocation(industryAllocations);
    const isInvalidForm = !isValidLocalBlend(this.state);

    const blendObject = {
      ...this.state,
      revenueUpliftPercentage: revenueUpliftPercentage
        ? Number(revenueUpliftPercentage)
        : undefined,
      industryAllocations: industryAllocations.map((allocation) => {
        return {
          ...allocation,
          providerIndustries: [
            {
              provider: INDUSTRY_PROVIDERS.COMPUTER_ECONOMICS,
              code: allocation.computerEconomicsIndustryCode,
            },
            {
              provider: INDUSTRY_PROVIDERS.GARTNER,
              code: allocation.gartnerIndustryCode,
            },
          ],
        };
      }),
    };
    const currentBlendName = industryBlend && industryBlend.name;
    const blendFromList = industryBlends.find(
      (blend) => blend.name.toLowerCase().trim() === name.toLowerCase().trim()
    );
    const isExistingBlendName = Boolean(
      blendFromList && blendFromList.name !== currentBlendName
    );
    return (
      <>
        <SectionTitle>Industry blend configurations</SectionTitle>
        <SectionDescription>
          Configure the blend of industries
        </SectionDescription>
        <IndustryBlendForm
          addIndustry={() => {
            const newIndustryAllocations = [
              ...industryAllocations,
              getEmptyIndustry(totalAllocation),
            ];
            this.setState({ industryAllocations: newIndustryAllocations });
          }}
          apIndustries={apIndustries}
          availableYears={availableYears}
          industries={industryAllocations.map((allocation) => ({
            ...allocation,
            isMissingData: missingProviders.find(
              (provider) => provider.industryCode === provider.code
            ),
          }))}
          industryBlendName={name}
          isExistingBlendName={isExistingBlendName}
          missingDataProviders={missingDataProviders}
          missingProviders={missingProviders}
          providerIndustries={providerIndustries}
          removeIndustry={(id) => {
            const newIndustryAllocations = [...industryAllocations];
            newIndustryAllocations.splice(id, 1);
            this.setState({ industryAllocations: newIndustryAllocations });
          }}
          resetIndustry={(id) => {
            const newIndustryAllocations = [...industryAllocations];
            newIndustryAllocations.splice(
              id,
              1,
              getEmptyIndustry(totalAllocation)
            );
            this.setState({ industryAllocations: newIndustryAllocations });
          }}
          revenueUpliftPercentage={revenueUpliftPercentage}
          setRevenueUpliftPercentage={(revenueUpliftPercentage) => {
            this.setState({ revenueUpliftPercentage });
          }}
          setIndustryBlendName={(name) => {
            this.setState({ name });
          }}
          setYear={(year) => {
            this.setState({ year: Number(year) });
          }}
          updateIndustries={this.updateIndustriesAllocation}
          year={String(year)}
        />

        <IndustryFormActionRow>
          <ApButtonDanger onClick={onCancel}>Cancel</ApButtonDanger>

          <SecondaryBlendButton
            disabled={
              isInvalidForm ||
              isExistingBlendName ||
              missingProviders.length !== 0
            }
            onClick={() => {
              onCreate(blendObject);
            }}
          >
            Save industry blend
          </SecondaryBlendButton>

          <ApButtonMain
            disabled={
              isInvalidForm ||
              blendObject.isActive ||
              isExistingBlendName ||
              missingProviders.length !== 0
            }
            onClick={() => {
              onCreateAndApply(blendObject);
            }}
          >
            Save and apply Industry blend
          </ApButtonMain>
        </IndustryFormActionRow>
      </>
    );
  }
}
